import React, { Component } from 'react';
import { FormattedMessage } from '../../util/reactIntl';
import classNames from 'classnames';
import { propTypes } from '../../util/types';
import { getZoomLevelForRadius, milesToMeters, obfuscatedCoordinates } from '../../util/maps';
import { Heading, Map } from '../../components';

import css from './ListingPage.module.css';

/**
 * If the listing has a positive `serviceArea` (miles — e.g. "how far are you willing to travel?")
 * in its public data, returns Map props that size and zoom the fuzzy circle to match it, instead
 * of the default privacy-offset circle. Only the drawn circle's radius (and the zoom needed to
 * show it) changes; the circle's center stays the regular, small privacy-offset obfuscated point,
 * so a large service area is never confused with a jump in how much the center itself is fuzzed.
 *
 * Returns an empty object when there's no usable service area, so the caller can spread the
 * result in and get the map's ordinary default sizing unchanged.
 *
 * @param {propTypes.latlng} [geolocation] - listing's geolocation (used for a latitude-aware zoom)
 * @param {Object} [publicData] - listing's public data, which may have a numeric `serviceArea`
 * @param {Object} mapsConfig - marketplace's map configuration (`config.maps`)
 * @returns {{circleRadius?: number, zoom?: number}}
 */
export const getServiceAreaMapOverride = (geolocation, publicData, mapsConfig) => {
  const serviceArea = publicData?.serviceArea;
  const hasServiceArea = typeof serviceArea === 'number' && serviceArea > 0;

  if (!hasServiceArea) {
    return {};
  }

  const circleRadius = milesToMeters(serviceArea);
  const zoom = getZoomLevelForRadius(circleRadius, {
    latitude: geolocation?.lat,
    maxZoom: mapsConfig.fuzzy.defaultZoomLevel,
  });

  return { circleRadius, zoom };
};

/**
 * The SectionMapMaybe component.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.rootClassName] - Custom class that overrides the default class for the root element
 * @param {propTypes.latlng} [props.geolocation] - The geolocation
 * @param {propTypes.uuid} props.listingId - The listing id
 * @returns {JSX.Element} section map maybe component
 */
class SectionMapMaybe extends Component {
  constructor(props) {
    super(props);
    this.state = { isStatic: true };
  }

  render() {
    const {
      className,
      rootClassName,
      geolocation,
      publicData,
      listingId,
      mapsConfig,
      name,
    } = this.props;
    const firstName = typeof name === 'string' ? name.trim().split(/\s+/)[0] : '';

    if (!geolocation) {
      return null;
    }

    const address = publicData && publicData.location ? publicData.location.address : '';
    const classes = classNames(rootClassName || css.sectionMap, className);
    const cacheKey = listingId ? `${listingId.uuid}_${geolocation.lat}_${geolocation.lng}` : null;

    const mapProps = mapsConfig.fuzzy.enabled
      ? {
          obfuscatedCenter: obfuscatedCoordinates(geolocation, mapsConfig.fuzzy.offset, cacheKey),
          ...getServiceAreaMapOverride(geolocation, publicData, mapsConfig),
        }
      : { address, center: geolocation };
    const map = <Map {...mapProps} useStaticMap={this.state.isStatic} />;

    return (
      <section className={classes} id="listing-location">
        <Heading as="h2" rootClassName={css.sectionHeadingWithExtraMargin}>
          {firstName ? (
            <FormattedMessage id="ListingPage.profile.locationTitle" values={{ name: firstName }} />
          ) : (
            <FormattedMessage id="ListingPage.locationTitle" />
          )}
        </Heading>
        {this.state.isStatic ? (
          <button
            className={css.map}
            onClick={() => {
              this.setState({ isStatic: false });
            }}
          >
            {map}
          </button>
        ) : (
          <div className={css.map}>{map}</div>
        )}
      </section>
    );
  }
}

export default SectionMapMaybe;
